// Article content rendered to HTML ahead of the build by
// scripts/generate-static-content.mjs. Edit these strings, then run
// `yarn generate:content` to refresh src/staticContent.generated.js.
//
// The keys are stable names used by <Equation> and <CodeBlock> in App.jsx.

export const equations = {
  'newton-with-i': 'z_{n+1} = z_n - \\frac{z_n^3 - 1}{3z_n^2}, \\quad z_0 = x_0 + iy_0, \\quad{i = \\sqrt{-1}}',
  'newton-general-iteration': 'z_{n+1} = z_n - a\\frac{p(z_n)}{p\'(z_n)}',
  'newton-cubic-polynomial': 'p(z) = z^3 - 1, \\quad p\'(z) = 3z^2',
  'newton-cube-roots': 'r_0 = 1, \\quad r_1 = -\\frac{1}{2} + i\\frac{\\sqrt{3}}{2}, \\quad r_2 = -\\frac{1}{2} - i\\frac{\\sqrt{3}}{2}',
  'newton-smale-cubic': 'p_S(z) = z^3 - 2z + 2, \\quad N_S(z) = \\frac{2z^3 - 2}{3z^2 - 2}, \\quad 0 \\mapsto 1 \\mapsto 0',
  'complex-number': 'z_0 = x_0 + iy_0, \\quad{i = \\sqrt{-1}}',
  'complex-parameter': 'a = a_x + ia_y',
  'real-part-iteration': 'x_{n+1} = \\frac{2}{3}x_n + \\frac{x_n^2 - y_n^2}{3(x_n^2 + y_n^2)^2}',
  'imaginary-part-iteration': 'y_{n+1} = \\frac{2}{3}y_n - \\frac{2x_ny_n}{3(x_n^2 + y_n^2)^2}',
};

// Code snippets are stored trimmed; the renderer highlights them as-is.
export const codeSnippets = {
  'iterate-until-escape': `const CONVERGENCE_THRESHOLD = 1e-4;
const MAX_ITERATIONS = 1024;
const ROOTS = [
  [1, 0],
  [-0.5, Math.sqrt(3) / 2],
  [-0.5, -Math.sqrt(3) / 2]
];

function iterateUntilConvergence(x0, y0) {
  let x = x0, y = y0;
  for (let n = 1; n <= MAX_ITERATIONS; n++) {
    const r2 = x*x + y*y;
    const r4 = Math.max(1e-30, r2*r2);
    const sx = (x - (x*x - y*y) / r4) / 3;
    const sy = (y + (2*x*y) / r4) / 3;
    x -= sx;
    y -= sy;
    for (let k = 0; k < 3; k++) {
      const dx = x - ROOTS[k][0], dy = y - ROOTS[k][1];
      const d2 = dx*dx + dy*dy;
      if (d2 < CONVERGENCE_THRESHOLD) {
        return [n, d2, k, true];
      }
    }
  }
  return [MAX_ITERATIONS, Infinity, 0, false];
}`,
  'iteration-color-palette': `function getColor(numIterations, rootIndex) {
  return [
    255,                // red
    numIterations * 7,  // green
    rootIndex * 85,     // blue (root basin tint)
    numIterations * 15  // alpha
  ];
}`,
  'draw-fractal-canvas': `function drawNewtonFractal(canvasEl, xRange, yRange, getColor) {
  const canvasWidth = canvasEl.width;
  const canvasHeight = canvasEl.height;
  const context = canvasEl.getContext('2d');
  const canvasImageData =
    context.createImageData(canvasWidth, canvasHeight);
  const image = canvasImageData.data;
  for (let i = 0; i < canvasHeight; i++) {
    for (let j = 0; j < canvasWidth; j++) {
      const x0 = xRange[0] +
        j*(xRange[1] - xRange[0]) / canvasWidth;
      const y0 = yRange[0] +
        i*(yRange[1] - yRange[0]) / canvasHeight;
      const [numIterations, rootDistanceSq, root, converged] =
        iterateUntilConvergence(x0, y0);
      const pixelIndex = 4*i*canvasWidth + 4*j;
      if (converged) {
        const mu = getMu(numIterations, rootDistanceSq);
        const pixels = getColor({ numIterations, mu, root });
        for (let p = 0; p < 4; p++) {
          image[pixelIndex + p] = pixels[p];
        }
      }
    }
  }
  context.putImageData(canvasImageData, 0, 0);
}`,
  'escape-time-coloring': `const CONVERGENCE_THRESHOLD = 1e-4;

function getMu(numIterations, rootDistanceSq) {
  const logRatio = Math.log(rootDistanceSq) /
    Math.log(CONVERGENCE_THRESHOLD);
  return numIterations - Math.log(logRatio) / Math.LN2;
}

function getColor(mu, root) {
  return [ 255, mu * 7, root * 85, mu * 15 ];
}`,
};
